// Pure axis math for the axis-snapped views (2D floor plan + model ortho): the
// world-axis sets each can look down, the nearest axis to a direction, and the
// up-vector resolution (canonical roll-reset vs roll-preserving). No scene
// state — just THREE vectors.

import * as THREE from "three";

// The world axes the plan can look down. −Y (looking up at the floor's
// underside) is intentionally omitted — a floor plan is never viewed from below.
export const PLAN_AXES = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(-1, 0, 0),
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(0, 0, 1),
  new THREE.Vector3(0, 0, -1),
];
// All six axes — the model's orthographic view can look down any of them
// (unlike the floor plan, which omits the −Y underside).
export const MODEL_AXES = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(-1, 0, 0),
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(0, -1, 0),
  new THREE.Vector3(0, 0, 1),
  new THREE.Vector3(0, 0, -1),
];

export function nearestAxisIn(dir: THREE.Vector3, axes: THREE.Vector3[]): THREE.Vector3 {
  let best = axes[0];
  let bestDot = -Infinity;
  for (const a of axes) {
    const d = dir.dot(a);
    if (d > bestDot) {
      bestDot = d;
      best = a;
    }
  }
  return best;
}
// Up vector for an axis view: top/bottom (±Y) use ∓Z so +X reads right; the
// four side views use +Y. This is the canonical (roll-reset) orientation.
export function planUpForAxis(axis: THREE.Vector3, out: THREE.Vector3): void {
  if (Math.abs(axis.y) > 0.9) out.set(0, 0, axis.y > 0 ? -1 : 1);
  else out.set(0, 1, 0);
}
// Roll-preserving up for an axis view: snap `currentUp` to the nearest cardinal
// axis perpendicular to `axis`. Keeps the orientation continuous across a swipe
// (the up you rotated to is carried over) instead of resetting to canonical.
export function snapUpForAxis(
  axis: THREE.Vector3,
  currentUp: THREE.Vector3,
  out: THREE.Vector3,
): void {
  let bestDot = -Infinity;
  for (const c of MODEL_AXES) {
    if (Math.abs(c.dot(axis)) > 0.5) continue; // keep only ⟂ candidates
    const d = c.dot(currentUp);
    if (d > bestDot) {
      bestDot = d;
      out.copy(c);
    }
  }
}
